import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * Related Articles — pulls up to 3 posts sharing at least one tag with the current post.
 * Falls back to 3 most-recent OTHER posts if no tag overlap.
 */
export default function RelatedArticles({ currentSlug, tags = [] }) {
    const [related, setRelated] = useState([]);
    useEffect(() => {
        api.get("/blog/posts?limit=50").then(({ data }) => {
            const others = data.filter((p) => p.slug !== currentSlug);
            const tagSet = new Set((tags || []).map((t) => t.toLowerCase()));
            // Score each post by tag overlap
            const scored = others.map((p) => {
                const overlap = (p.tags || []).filter((t) => tagSet.has(t.toLowerCase())).length;
                return { post: p, overlap };
            });
            // Prefer tag matches; break ties by recency (posts already come sorted desc)
            scored.sort((a, b) => b.overlap - a.overlap);
            setRelated(scored.slice(0, 3).map((s) => s.post));
        }).catch(() => setRelated([]));
    }, [currentSlug, tags?.join(",")]);

    if (!related.length) return null;

    return (
        <section className="max-w-6xl mx-auto px-6 py-16 border-t border-forest/20" data-testid="related-articles">
            <div className="font-mono text-xs uppercase tracking-widest text-clay mb-4">[ Keep reading ]</div>
            <h2 className="font-serif text-3xl lg:text-5xl text-forest tracking-tight mb-10">Related articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
                {related.map((p) => (
                    <Link
                        key={p.slug}
                        to={`/blog/${p.slug}`}
                        className="group block"
                        data-testid={`related-article-${p.slug}`}
                    >
                        {p.cover_image && (
                            <div className="image-frame aspect-[4/3] mb-4 overflow-hidden">
                                <img
                                    src={p.cover_image}
                                    alt={p.title}
                                    loading="lazy"
                                    className="transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                        )}
                        <div className="font-mono text-[11px] uppercase tracking-widest text-clay mb-2">
                            {(p.tags || []).slice(0, 2).join(" · ")}
                        </div>
                        <h3 className="font-serif text-xl lg:text-2xl leading-tight text-forest group-hover:text-clay transition-colors mb-3">
                            {p.title}
                        </h3>
                        <p className="text-sm text-inkgreen/75 line-clamp-3">{p.excerpt}</p>
                        <div className="mt-4 inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-clay">
                            Read <ArrowUpRight className="w-3.5 h-3.5" />
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
}
